import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { defaultRouter } from 'app/src/router';
import { Analytics, SourceType } from '@trello/atlassian-analytics';
import { ViewFilter, BoardsFilter } from './filters';
import { navigate } from 'app/scripts/controller/navigate';
import { forTemplate } from '@trello/i18n';
import {
  ViewFiltersContext,
  ViewFiltersContextValue,
  ViewFiltersSource,
} from './ViewFiltersContext';
import { showFlag } from '@trello/nachos/experimental-flags';
import { ViewFilters } from './ViewFilters';
import WorkspaceViewsQuery from 'app/src/components/WorkspaceNavigation/WorkspaceViewsQuery.graphql';
import { useViewFiltersCreateOrganizationViewMutation } from './ViewFiltersCreateOrganizationViewMutation.generated';
import { Urls } from 'app/scripts/controller/urls';
import { useFeatureFlag } from '@trello/feature-flag-client';
import { usePrePopulatedBoardsFilter } from './usePrePopulatedBoardsFilter';
import { ProductFeatures } from '@trello/product-features';
import { useUrlParamsProviderWorkspaceQuery } from './UrlParamsProviderWorkspaceQuery.generated';
import { useFixForBackboneNavigationBug, useUrlParams } from './useUrlParams';
import { workspaceViewNavigationState } from 'app/src/components/WorkspaceNavigation/workspaceViewNavigationState';
import { useSharedState } from '@trello/shared-state';

const { getWorkspaceViewUrl } = Urls;

const format = forTemplate('organization_view');

interface UrlParamsProviderProps {
  viewType?: string;
  idOrg?: string;
  children: React.ReactNode;
}

export const UrlParamsProvider: React.FC<UrlParamsProviderProps> = ({
  viewType,
  idOrg,
  children,
}) => {
  // To repro the bug this fixes: Have a table with 1 board, no filters.
  // Refresh, then try to remove the board. Without this fix, nothing will
  // happen. With this fix, you can remove the board.
  useFixForBackboneNavigationBug();

  const urlParams = useUrlParams();

  const viewFiltersFromUrlParams: ViewFilters = ViewFilters.fromQueryParams(
    urlParams,
  );

  const [
    createOrganizationView,
    { data, loading, error },
  ] = useViewFiltersCreateOrganizationViewMutation();

  useEffect(() => {
    if (data && !loading) {
      if (data.createOrganizationView) {
        navigate(getWorkspaceViewUrl(data.createOrganizationView), {
          trigger: true,
        });
      }
    }
  }, [data, loading]);

  const navigateToUrl = useCallback(
    (newViewFilters: ViewFilters, stripPopulateParam: boolean = false) => {
      const routeContext = defaultRouter.getRoute();
      const newUrl = ViewFilters.constructNewUrl(routeContext)?.(
        newViewFilters,
      );

      if (
        !newUrl?.searchParams.has('populate') &&
        routeContext.url?.toString() === newUrl?.toString()
      ) {
        return;
      }

      if (stripPopulateParam) {
        // Removes the populate flag and makes sure that the URL gets unencoded.
        newUrl?.searchParams.delete('populate');
        if (newUrl)
          newUrl.search = newUrl?.search
            .replace(/%2C/g, ',')
            .replace(/%3A/g, ':');
      }

      navigate(`${newUrl?.pathname}${newUrl?.search}`, { replace: true });
      defaultRouter.updateSubscribers();
    },
    [],
  );

  const navigateWithUrlParams = useCallback(
    (filter: ViewFilter, stripPopulateParam: boolean = false) => {
      const newViewFilters = viewFiltersFromUrlParams.setFilter(filter);
      navigateToUrl(newViewFilters, stripPopulateParam);
    },
    [viewFiltersFromUrlParams, navigateToUrl],
  );

  const {
    data: workspaceData,
    loading: workspaceLoading,
  } = useUrlParamsProviderWorkspaceQuery({
    variables: { idWorkspace: idOrg ?? '' },
  });

  const populateViewFlagEnabled = useFeatureFlag(
    'panorama.populate-custom-workspace-views',
    false,
  );
  const isFreeTeam = Boolean(!workspaceData?.organization?.products?.length);
  const isStandardTeam = ProductFeatures.isStandardProduct(
    workspaceData?.organization?.products?.[0],
  );
  const runPopulate =
    populateViewFlagEnabled &&
    !isFreeTeam &&
    !isStandardTeam &&
    urlParams['populate'] !== undefined;

  const hasPopulateParam = useMemo(() => urlParams['populate'] !== undefined, [
    urlParams,
  ]);

  // Logic for pre-populating relevant boards. If the URL Param "populate" is
  // present, we apply some board filters to the params and remove that flag.
  const {
    loading: relevantBoardsLoading,
    boards: relevantBoards,
  } = usePrePopulatedBoardsFilter(idOrg ?? '', !runPopulate);

  useEffect(() => {
    if (
      !workspaceLoading &&
      !relevantBoardsLoading &&
      urlParams['populate'] !== undefined
    ) {
      // For workspaces with views, autopopulate boards.
      if (runPopulate && relevantBoards) {
        const boardsFilter = new BoardsFilter(relevantBoards);
        navigateWithUrlParams(boardsFilter, true);
      }
      // For workspaces without views, this strips the populate param.
      else {
        navigateWithUrlParams(viewFiltersFromUrlParams.boards, true);
      }
    }
  }, [
    relevantBoards,
    navigateWithUrlParams,
    runPopulate,
    urlParams,
    relevantBoardsLoading,
    hasPopulateParam,
    viewFiltersFromUrlParams.boards,
    workspaceLoading,
  ]);

  const clearFilters: ViewFiltersSource['clearFilters'] = useCallback(
    (skip = ['boards', 'sort']) => {
      const emptyFilters = viewFiltersFromUrlParams.clearFilters(skip);
      navigateToUrl(emptyFilters);
    },
    [viewFiltersFromUrlParams, navigateToUrl],
  );

  const getCommonAttributes = useCallback(
    () => ({
      totalFilterLength: viewFiltersFromUrlParams.totalFilterLength([
        'boards',
        'sort',
      ]),
    }),
    [viewFiltersFromUrlParams],
  );

  const [filterPopoverResult, setFilterPopoverResult] = useState<
    ViewFiltersSource['filterPopoverResult']
  >();

  const taskName =
    viewType === 'Table'
      ? 'create-organizationView/table'
      : 'create-organizationView/calendar';

  const [workspaceViewNavigationFilter] = useSharedState(
    workspaceViewNavigationState,
  );

  const saveFilters = useCallback(
    async (idBoards: Array<string>, viewSource: SourceType) => {
      if (idOrg) {
        const permissionLevel = 'private';
        const name = format('untitled');

        const cardFilterData = viewFiltersFromUrlParams.toCardFilterCriteria(
          idBoards,
        );
        const traceId = Analytics.startTask({
          taskName,
          source: viewSource,
        });

        try {
          await createOrganizationView({
            variables: {
              name: name,
              idOrganization: idOrg,
              prefs: { permissionLevel },
              views: [
                {
                  cardFilter: {
                    criteria: [{ ...cardFilterData }],
                  },
                  defaultViewType: viewType,
                },
              ],
              traceId,
            },
            refetchQueries: [
              {
                query: WorkspaceViewsQuery,
                variables: {
                  orgId: idOrg,
                  organizationViewsFilter:
                    workspaceViewNavigationFilter['filter'],
                },
              },
            ],
          });

          Analytics.sendTrackEvent({
            action: 'created',
            actionSubject: 'workspaceView',
            source: viewSource,
            attributes: {
              taskId: traceId,
              location: 'Filters',
              permissionLevel: permissionLevel,
              defaultViewType: viewType,
            },
          });

          Analytics.taskSucceeded({
            source: viewSource,
            taskName,
            traceId,
          });
        } catch (err) {
          Analytics.taskFailed({
            source: viewSource,
            error: err,
            taskName,
            traceId,
          });
          showFlag({
            id: 'WorkspaceViewFilters',
            title: format('failed-to-create-view'),
            appearance: 'error',
            msTimeout: 5000,
          });
        }
      } else if (error) {
        // TODO: Error State.
      }
    },
    [
      taskName,
      idOrg,
      viewFiltersFromUrlParams,
      createOrganizationView,
      viewType,
      error,
      workspaceViewNavigationFilter,
    ],
  );

  const providerValue: ViewFiltersContextValue<ViewFiltersSource> = useMemo(() => {
    return {
      viewFilters: {
        loading: relevantBoardsLoading,
        contextType: 'urlParams',
        filters: viewFiltersFromUrlParams,
        setFilter: navigateWithUrlParams,
        clearFilters,
        resetFilters: clearFilters,
        saveFilters,
        getCommonAttributes,
        filterPopoverResult,
        setFilterPopoverResult,
      },
    };
  }, [
    relevantBoardsLoading,
    viewFiltersFromUrlParams,
    navigateWithUrlParams,
    clearFilters,
    saveFilters,
    getCommonAttributes,
    filterPopoverResult,
    setFilterPopoverResult,
  ]);

  return (
    <ViewFiltersContext.Provider value={providerValue}>
      {children}
    </ViewFiltersContext.Provider>
  );
};
